"use client";

import { Brain, Heart, Waves, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ManufacturingExcellence() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const quote = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const features = [{
    number: "01",
    title: "Cold Storage Integrity",
    description: "Our peptides are maintained in strictly controlled, sub-zero environments. By preventing thermal degradation, we ensure that every vial retains its full molecular potency from the lab to your research facility.",
    icon: Brain,
    offset: "lg:mt-0"
  }, {
    number: "02",
    title: "HPLC Lab Verification",
    description: "Every batch undergoes rigorous High-Performance Liquid Chromatography testing. We verify purity levels to ensure your research is conducted with compounds that meet the highest industrial standards.",
    icon: Heart,
    offset: "lg:mt-20"
  }, {
    number: "03",
    title: "COA Transparency",
    description: "We provide full Certificate of Analysis documentation for every lot. Our commitment to data-driven quality means you have complete visibility into the purity and composition of your research materials.",
    icon: Waves,
    offset: "lg:mt-0"
  }];
  return <section data-section-id="938" id="manufacturing-excellence" className="bg-background relative overflow-hidden py-24 md:py-32 lg:py-40">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-40" style={{
      background: "radial-gradient(circle at 50% 30%, hsla(45, 35%, 55%, 0.06) 0%, transparent 60%)"
    }} />

      <div className="relative container mx-auto px-6">
        <div ref={intro.ref} className={`mx-auto max-w-[720px] text-center transition-all duration-1000 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-default text-xs uppercase tracking-[0.3em] text-primary">
            Quality Standards
          </p>
          <h2 className="font-default font-bold text-foreground mt-8 leading-[1.05]" style={{
          fontSize: "clamp(3rem, 6vw, 5rem)"
        }}>
            Precision
            <br />
            in <em className="italic font-normal">every batch</em>
          </h2>
          <p className="font-default text-foreground/70 mt-10 text-[20px] leading-relaxed">
            At Iron Habit Peptides, we treat research materials with the same 
            rigor as high-performance engineering. Our manufacturing process 
            is built on absolute purity, cold-chain reliability, and 
            uncompromising laboratory standards.
          </p>
        </div>

        <div className="mt-[100px] grid grid-cols-1 gap-16 md:gap-20 lg:grid-cols-3 lg:gap-12">
          {features.map((feature, i) => {
          const Icon = feature.icon;
          const card = useRevealOnScroll<HTMLDivElement>({
            threshold: 0.15
          });
          return <div data-index={i} ref={card.ref} key={feature.number} className={`flex flex-col ${feature.offset} transition-all duration-1000 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            transitionDelay: `${i * 150}ms`
          }}>
                <Icon className="text-primary" strokeWidth={1} size={60} aria-hidden="true" />
                <p className="font-default text-xs tracking-[0.2em] text-primary mt-10">
                  {feature.number}
                </p>
                <h3 className="font-default font-semibold text-foreground mt-4 text-[28px] leading-tight">
                  {feature.title}
                </h3>
                <p className="font-default text-foreground/70 mt-5 text-[15px] leading-relaxed">
                  {feature.description}
                </p>
                <Separator className="bg-border mt-10" />
                <Link to="/PerformanceStandardsPage" className="font-default text-foreground hover:text-primary mt-5 inline-block text-xs uppercase tracking-[0.25em] transition-colors">
                  View Standards
                </Link>
              </div>;
        })}

          <div data-index={3} className="lg:col-span-3 mt-12 lg:mt-24">
            <FourthFeature />
          </div>
        </div>

        <div ref={quote.ref} className={`mx-auto mt-32 max-w-[1000px] text-center md:mt-40 transition-all duration-1000 ease-out ${quote.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <blockquote className="font-default italic font-light text-foreground leading-[1.15]" style={{
          fontSize: "clamp(2rem, 4vw, 3rem)"
        }}>
            Quality is not an act,
            <br />
            it is the habit of
            <br />
            absolute precision.
          </blockquote>
          <p className="font-default text-foreground/60 mt-10 text-xs uppercase tracking-[0.3em]">
            IRON HABIT — EST. 2024
          </p>
        </div>
      </div>
    </section>;
}
function FourthFeature() {
  const card = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={card.ref} className={`mx-auto grid max-w-5xl grid-cols-1 items-start gap-10 md:grid-cols-12 md:gap-14 transition-all duration-1000 ease-out ${card.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="md:col-span-4 flex justify-start md:justify-center">
        <Sparkles className="text-primary" strokeWidth={1} size={60} aria-hidden="true" />
      </div>
      <div className="md:col-span-8 flex flex-col">
        <p className="font-default text-xs tracking-[0.2em] text-primary">04</p>
        <h3 className="font-default font-semibold text-foreground mt-4 text-[28px] leading-tight md:text-[32px]">
          Batch Consistency
        </h3>
        <p className="font-default text-foreground/70 mt-5 text-[15px] leading-relaxed max-w-2xl">
          We maintain strict lot-to-lot consistency through standardized 
          synthesis protocols. By minimizing variance, we ensure that your 
          long-term research remains stable and reproducible, providing the 
          reliability required for elite-level performance studies.
        </p>
        <Separator className="bg-border mt-10" />
        <Link to="/VerificationHubPage" className="font-default text-foreground hover:text-primary mt-5 inline-block text-xs uppercase tracking-[0.25em] transition-colors">
          Verify Batch
        </Link>
      </div>
    </div>;
}